/**
 * Sites, nodes and enrolment on the wire (PLAN §3, §8, §9.1, D10, D16, D21).
 *
 * A **site** is where a tenant's nodes run: an on-prem studio (`onprem`), or
 * the tenant's one automatic **cloud site** (`cloud`, id `cloud`), whose nodes
 * are the Fargate tasks of cloud productions (§9.1; they enrol in phase 6).
 * A **node** is one ACP container, enrolled into a site with a one-time code
 * (D21): the first becomes `main`, the second `backup`, and a third is refused
 * unless it enrols as a `witness` (D9's LAN witness).
 *
 * `.strict()` as the rest of the contract; every id is a key segment
 * (`entityIdSchema`), so none can forge a DynamoDB key.
 */
import { z } from 'zod';
import { siteIdSchema, studioIdSchema } from './studios.js';
import { entityIdSchema, tenantIdSchema } from './tenancy.js';
import { witnessInForceSchema } from './witness.js';

/** A node id: a ULID, so node ids are unique across tenants and sort by enrolment time. */
export const nodeIdSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'node ids are ULIDs');

/** The automatic cloud site's id — the same in every tenant (D10, §9.1). */
export const CLOUD_SITE_ID = 'cloud';

export const siteKindSchema = z.enum(['onprem', 'cloud']);
export type SiteKind = z.infer<typeof siteKindSchema>;

/** A node's role in its site (D9, D21). Per-production roles (active/standby) are the productions' own (§8.1). */
export const siteNodeRoleSchema = z.enum(['main', 'backup', 'witness']);
export type SiteNodeRole = z.infer<typeof siteNodeRoleSchema>;

/** `revoked`: replaced (D21), its key refused everywhere from then on. */
export const nodeStatusSchema = z.enum(['active', 'revoked']);
export type NodeStatus = z.infer<typeof nodeStatusSchema>;

/** The site link as the cloud sees it (PLAN §10.2) — the same values `GET /status` reports. */
export const siteLinkStateSchema = z.enum(['connected', 'disconnected', 'never-connected']);

/** A raw 32-byte public key (Ed25519 or X25519), unpadded base64url: 43 characters. */
export const rawPublicKeySchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'a raw 32-byte public key, base64url (43 characters)');

/**
 * A node's LAN address as the janitor reports it (D16): IPv4 in a private
 * range only (RFC 1918, or 100.64/10 carrier-grade NAT), because the cloud
 * publishes it in public DNS under our zone with a public certificate for the
 * name — a public address there would lend our name and a valid certificate to
 * whatever answers on it.
 */
export const lanIpSchema = z.ipv4().refine(isPrivateIpv4, { message: 'the LAN IP must be a private IPv4 address (10/8, 172.16/12, 192.168/16 or 100.64/10)' });

export function isPrivateIpv4(ip: string): boolean {
  const parts = ip.split('.').map(Number);
  const [a, b] = parts;
  if (parts.length !== 4 || a === undefined || b === undefined) return false;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127);
}

export const archSchema = z.enum(['x86_64', 'arm64']);

const isoSchema = z.iso.datetime({ offset: true });
const siteNameSchema = z.string().trim().min(1).max(200);
const labelSchema = z.string().trim().min(1).max(100);
/** A package's content hash (D15, PLAN §4.2): sha256, hex. */
export const packageHashSchema = z.string().regex(/^[0-9a-f]{64}$/, 'a package hash is sha256, lower-case hex');

export const siteSchema = z
  .object({
    siteId: siteIdSchema,
    name: z.string(),
    kind: siteKindSchema,
    /** The studios that name this site (`siteId`), by id. */
    studios: z.array(studioIdSchema),
    createdAt: isoSchema,
    updatedAt: isoSchema,
  })
  .strict();
export type Site = z.infer<typeof siteSchema>;

/** The last health report's gist (PLAN §11.1), or null before the first. The full reports are kept 7 days. */
export const nodeHealthSummarySchema = z
  .object({
    reportedAt: isoSchema,
    cpuPercent: z.number().min(0).max(100).nullable(),
    memoryUsedPercent: z.number().min(0).max(100).nullable(),
    diskUsedPercent: z.number().min(0).max(100).nullable(),
    clockDriftMs: z.number().nullable(),
    outboxDepth: z.number().int().nonnegative().nullable(),
    productions: z.number().int().nonnegative(),
    /** Which witness is in force on the node and its expected failover time, as its last report said (wave 4c). */
    witness: witnessInForceSchema.nullable().optional(),
  })
  .strict();

export const nodeSchema = z
  .object({
    nodeId: nodeIdSchema,
    siteId: siteIdSchema,
    role: siteNodeRoleSchema,
    label: z.string(),
    lanIp: z.string().nullable(),
    arch: archSchema,
    /** The node's Ed25519 public key, raw base64url: what verifies its signed requests. */
    publicKey: rawPublicKeySchema,
    status: nodeStatusSchema,
    enrolledAt: isoSchema,
    revokedAt: isoSchema.nullable(),
    /** When the cloud last heard from the node's site link, or null if never. */
    lastSeen: isoSchema.nullable(),
    linkState: siteLinkStateSchema,
    /** The packages the node reported holding (D15). */
    packageHashes: z.array(packageHashSchema),
    /** The node's local HTTPS name (D16), or null for a node without one (a cloud node, or no LAN IP yet). */
    dnsName: z.string().nullable(),
    /** When its current certificate expires, or null before the first is issued. */
    certExpiresAt: isoSchema.nullable(),
    health: nodeHealthSummarySchema.nullable(),
  })
  .strict();
export type SiteNode = z.infer<typeof nodeSchema>;

// ─── requests and responses ───

export const sitePathSchema = z.object({ siteId: siteIdSchema.describe('The site’s id.') }).strict();
export const nodePathSchema = sitePathSchema.extend({ nodeId: nodeIdSchema.describe('The node’s id.') }).strict();

export const createSiteRequestSchema = z
  .object({
    /** A client-chosen id makes a retry idempotent. */
    siteId: entityIdSchema.refine((id) => id !== CLOUD_SITE_ID, { message: `"${CLOUD_SITE_ID}" is the automatic cloud site's id` }).optional(),
    name: siteNameSchema,
  })
  .strict();
export type CreateSiteRequest = z.infer<typeof createSiteRequestSchema>;

export const updateSiteRequestSchema = z.object({ name: siteNameSchema }).strict();

/** Enrolment codes last at most 24 h (D21); the portal's default. */
export const MAX_ENROLMENT_CODE_MINUTES = 24 * 60;

export const createEnrolmentCodeRequestSchema = z
  .object({
    /** How long the code is valid: 5 minutes to 24 hours (D21). */
    expiresInMinutes: z.number().int().min(5).max(MAX_ENROLMENT_CODE_MINUTES),
  })
  .strict();

/** `POST /sites/{siteId}/enrolment-codes`: optionally a witness code (D9, D21: a third node enrols only as the site's LAN witness). */
export const createSiteEnrolmentCodeRequestSchema = createEnrolmentCodeRequestSchema
  .extend({
    /** `witness`: the code enrols the site's LAN witness (its install line runs `--role witness`). Absent: the pairing order. */
    role: z.literal('witness').optional(),
  })
  .strict();

export const enrolmentCodeSchema = z
  .object({
    /**
     * The one-time code — `ACP1.<regional API URL, base64url>.<codeId>.<secret>` — shown once:
     * the cloud keeps only a hash of the secret.
     */
    code: z.string(),
    codeId: z.string(),
    siteId: siteIdSchema,
    expiresAt: isoSchema,
    /** A node enrolling with it takes this role (a replacement's), or null for the pairing order (D21). */
    role: siteNodeRoleSchema.nullable(),
    /** The node it replaces, or null. */
    replacesNodeId: nodeIdSchema.nullable(),
    /** The install line to run on the node's host (D21). */
    dockerRun: z.string(),
  })
  .strict();
export type EnrolmentCode = z.infer<typeof enrolmentCodeSchema>;

export const replaceNodeRequestSchema = createEnrolmentCodeRequestSchema;

export const swapRolesRequestSchema = z
  .object({
    /** The node that becomes main; the current main becomes backup. Naming the target makes a retry idempotent. */
    mainNodeId: nodeIdSchema,
  })
  .strict();

export const siteResponseSchema = z.object({ site: siteSchema }).strict();
export const listSitesResponseSchema = z.object({ sites: z.array(siteSchema) }).strict();
export const listNodesResponseSchema = z.object({ nodes: z.array(nodeSchema) }).strict();
export const enrolmentCodeResponseSchema = z.object({ enrolment: enrolmentCodeSchema }).strict();
export const replaceNodeResponseSchema = z.object({ node: nodeSchema, enrolment: enrolmentCodeSchema }).strict();

// ─── the enrolment exchange (D21), unauthenticated but code-gated ───

export const enrolRequestSchema = z
  .object({
    code: z.string().min(1).max(512),
    /** Ed25519, raw base64url: signs the node's requests from now on. */
    nodePublicKey: rawPublicKeySchema,
    /** X25519, raw base64url: what the cloud seals the node's TLS key and the site key to. */
    encryptionPublicKey: rawPublicKeySchema,
    label: labelSchema,
    lanIp: lanIpSchema,
    arch: archSchema,
    /** `witness` for a LAN witness (`--role witness`, D9); omitted for the pairing order. */
    role: z.literal('witness').optional(),
  })
  .strict();
export type EnrolRequest = z.infer<typeof enrolRequestSchema>;

/** A box sealed to a node's X25519 key (`@acp/cloud-link/seal.js`): X25519 → HKDF-SHA256 → AES-256-GCM. */
export const sealedBoxSchema = z
  .object({
    alg: z.literal('X25519-HKDF-SHA256-A256GCM'),
    /** The ephemeral public key, raw base64url. */
    epk: rawPublicKeySchema,
    nonce: z.string().regex(/^[A-Za-z0-9_-]{16}$/, 'a 96-bit nonce, base64url'),
    /** Ciphertext then the 16-byte GCM tag, base64url. */
    ct: z.string().regex(/^[A-Za-z0-9_-]+$/).max(64 * 1024),
  })
  .strict();
export type SealedBox = z.infer<typeof sealedBoxSchema>;

/**
 * The site's signing key (D20): an Ed25519 key shared by the site's nodes,
 * which sign local session tokens with it so either node accepts the other's.
 * The private half is sealed to the receiving node (context
 * `site-key:<siteId>:<nodeId>`).
 */
export const siteKeyDeliverySchema = z
  .object({
    /** Changes when the key is rotated; a node reports the one it holds. */
    keyId: z.string().min(1).max(64),
    publicKey: rawPublicKeySchema,
    sealedPrivateKey: sealedBoxSchema,
  })
  .strict();

export const enrolResponseSchema = z
  .object({
    tenantId: tenantIdSchema,
    siteId: siteIdSchema,
    nodeId: nodeIdSchema,
    role: siteNodeRoleSchema,
    /** Where this regional stack answers (D12): the REST API and the site link's WebSocket. */
    stack: z.object({ apiUrl: z.url(), wsUrl: z.url() }).strict(),
    /** What a node needs to verify Cognito tokens offline (D5, D20). */
    cognito: z.object({ issuer: z.url(), jwksUrl: z.url(), clientIds: z.array(z.string()) }).strict(),
    siteKey: siteKeyDeliverySchema,
    /** The node's local HTTPS name and the site alias (D16), or null (a cloud node). The certificate follows over the site link. */
    dns: z.object({ nodeName: z.string(), siteName: z.string() }).strict().nullable(),
  })
  .strict();
export type EnrolResponse = z.infer<typeof enrolResponseSchema>;

/** `GET /node/self`: the calling node as the cloud has it — the simplest signed call. */
export const nodeSelfResponseSchema = z.object({ tenantId: tenantIdSchema, node: nodeSchema }).strict();
